import React, { useCallback, useMemo, useState } from 'react';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import {
    useCostCategories,
    useCustomers,
    useProjects,
    useWorkTypes,
    selectedSortingType,
} from '~/app/data/statuses/issue';
import { useStoreState } from '~/store';
import { WorkOrderDetail, WorkOrderStatuses } from '~/workorder/types';
import {
    findCostCategory,
    findCustomer,
    shouldShowChargedDepartment,
    shouldShowCustomer,
    shouldShowInvoiceAcceptance,
    shouldShowProject,
    shouldShowSorting,
} from './adminFormHelper';
import { resolveOptionLabel } from './helper';
import EditPopup from './EditPopup';
import classes from './style.module.scss';

export type AdministrativeComponent = React.FC<{ data: WorkOrderDetail }>;

const Administrative: AdministrativeComponent = ({ data }) => {
    const { t } = useTranslation('workorder');
    const [expanded, setExpanded] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const workTypes = useStoreState(state => state.issue.workTypes);
    const costCategories = useStoreState(state => state.issue.costCategories);
    const customers = useStoreState(state => state.issue.customers);
    const projects = useStoreState(state => state.projects.list);
    const departments = useStoreState(state => state.app.appData.departments);

    const workTypeOptions = useWorkTypes(workTypes);
    const costCategoryOptions = useCostCategories(costCategories);
    const customerOptions = useCustomers(customers);
    const projectOptions = useProjects(projects);

    const sortingTypes = useMemo(() => [
        { id: 0, name: t('detail.sortingTypeOptions.0') },
        { id: 1, name: t('detail.sortingTypeOptions.1') },
    ], [t]);

    const departmentOptions = useMemo(
        () => departments?.map(dept => ({
            text: dept.name,
            value: dept.id,
        })) ?? [],
        [departments],
    );

    const category = useMemo(
        () => findCostCategory(costCategories, data.costCategoryId),
        [costCategories, data.costCategoryId],
    );

    const customer = useMemo(
        () => findCustomer(customers, data.customerId),
        [customers, data.customerId],
    );

    const sortingLabel = useMemo(
        () => selectedSortingType(sortingTypes, data.sortingType ?? ''),
        [sortingTypes, data.sortingType],
    );

    const rows = useMemo(() => {
        const items: { label: string; value: string | null | undefined }[] = [
            {
                label: t('detail.department'),
                value: resolveOptionLabel(departmentOptions, data.departmentId),
            },
            { label: t('detail.createdBy'), value: data.createdBy?.name },
        ];

        if (data.status === WorkOrderStatuses.APPROVED) {
            items.push({ label: t('detail.approvedBy'), value: data.approvedBy?.name });
        }

        items.push(
            { label: t('detail.responsible'), value: data.personnel?.name },
            { label: t('detail.workshop'), value: data.repairLocation?.trim() || null },
            {
                label: t('detail.workType'),
                value: resolveOptionLabel(workTypeOptions, data.workType),
            },
            {
                label: t('detail.costCategory'),
                value: resolveOptionLabel(costCategoryOptions, data.costCategoryId),
            },
        );

        if (shouldShowChargedDepartment(category)) {
            items.push({
                label: t('detail.chargedDepartment'),
                value: resolveOptionLabel(departmentOptions, data.chargedDepartmentId),
            });
        }

        if (shouldShowCustomer(category)) {
            items.push({
                label: t('detail.customer'),
                value: resolveOptionLabel(customerOptions, data.customerId),
            });
        }

        if (shouldShowCustomer(category) && shouldShowProject(customer)) {
            items.push({
                label: t('detail.project'),
                value: resolveOptionLabel(projectOptions, data.projectId),
            });
        }

        if (shouldShowCustomer(category) && shouldShowSorting(customer)) {
            items.push({
                label: t('detail.sortingType'),
                value: sortingLabel || null,
            });
        }

        if (shouldShowCustomer(category) && shouldShowInvoiceAcceptance(customer)) {
            items.push({
                label: t('detail.approvalInvoice'),
                value: data.invoiceAcceptanceUser,
            });
        }

        return items;
    }, [
        t,
        data,
        departmentOptions,
        workTypeOptions,
        costCategoryOptions,
        customerOptions,
        projectOptions,
        category,
        customer,
        sortingLabel,
    ]);

    const handleEditClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShowEdit(true);
    }, []);

    return (
        <div className={classes['card']}>
            <div className={classes['header']} onClick={() => setExpanded(prev => !prev)}>
                <h2 className={classes['title']}>{t('detail.administrativeInfo')}</h2>
                <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']} />
            </div>

            {expanded && (
                <>
                    <div className={classes['rows']}>
                        {rows.map(row => (
                            <div key={row.label} className={classes['row']}>
                                <span className={classes['label']}>{row.label}</span>
                                <span className={classes['value']}>{row.value?.trim() ?? '-'}</span>
                            </div>
                        ))}
                    </div>
                    {netWorkStatus === 0 && (
                        <button type="button" className={classes['edit-btn']} onClick={handleEditClick}>
                            <Icon name="edit" size={18} />
                            {t('detail.edit')}
                        </button>
                    )}
                </>
            )}

            <EditPopup
                data={data}
                show={showEdit}
                onClose={() => setShowEdit(false)}
            />
        </div>
    );
};

export default Administrative;
